import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import { isSupportedPhotoFile } from '../utils/image-file.utils';

export interface UploadingPhoto {
  id: string; // Unique identifier for tracking
  file: File;
  projectId: number;
  localPreviewUrl: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  progress: number;
  error?: string;
  uploadedPhoto?: any; // The response from server after upload
}

export interface UploadProgress {
  totalPhotos: number;
  uploadedPhotos: number;
  failedPhotos: number;
  isUploading: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PhotoUploadService {
  private uploadQueue: UploadingPhoto[] = [];
  private uploadQueueSubject = new BehaviorSubject<UploadingPhoto[]>([]);
  private progressSubject = new BehaviorSubject<UploadProgress>({
    totalPhotos: 0,
    uploadedPhotos: 0,
    failedPhotos: 0,
    isUploading: false
  });

  // Observable for components to subscribe to
  uploadQueue$ = this.uploadQueueSubject.asObservable();
  progress$ = this.progressSubject.asObservable();

  // Event when a single photo upload completes
  photoUploaded$ = new Subject<UploadingPhoto>();

  constructor(private apiService: ApiService) {}

  /**
   * Add photos to upload queue and start uploading
   */
  addPhotosToQueue(files: File[], projectId: number): void {
    const supportedFiles = files.filter(isSupportedPhotoFile);
    const newPhotos: UploadingPhoto[] = supportedFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      projectId,
      localPreviewUrl: URL.createObjectURL(file),
      status: 'pending',
      progress: 0
    }));

    this.uploadQueue = [...this.uploadQueue, ...newPhotos];
    this.uploadQueueSubject.next(this.uploadQueue);

    this.updateProgress();
    this.processQueue();
  }

  /**
   * Process upload queue - upload photos one by one
   */
  private async processQueue(): Promise<void> {
    const pendingPhotos = this.uploadQueue.filter(p => p.status === 'pending');

    if (pendingPhotos.length === 0) {
      return;
    }

    // Upload photos one at a time (can be modified to upload multiple in parallel)
    for (const photo of pendingPhotos) {
      await this.uploadPhoto(photo);
    }
  }

  /**
   * Upload a single photo
   */
  private uploadPhoto(photo: UploadingPhoto): Promise<void> {
    return new Promise((resolve) => {
      photo.status = 'uploading';
      this.uploadQueueSubject.next(this.uploadQueue);
      this.updateProgress();

      const formData = new FormData();
      formData.append('projectId', photo.projectId.toString());
      formData.append('files', photo.file);

      this.apiService.postWithoutSpinner('ProjectPhoto/AddProjectPhoto', formData).subscribe({
        next: (response) => {
          photo.status = 'completed';
          photo.progress = 100;
          photo.uploadedPhoto = response;
          this.uploadQueueSubject.next(this.uploadQueue);
          this.updateProgress();
          this.photoUploaded$.next(photo);

          // Remove completed photo from queue after 2 seconds
          setTimeout(() => {
            this.removePhoto(photo.id);
          }, 2000);

          resolve();
        },
        error: (error) => {
          photo.status = 'error';
          photo.error = error.message || 'Upload failed';
          this.uploadQueueSubject.next(this.uploadQueue);
          this.updateProgress();
          resolve(); // Continue with next upload even if this one failed
        }
      });
    });
  }

  /**
   * Update overall progress
   */
  private updateProgress(): void {
    const totalPhotos = this.uploadQueue.length;
    const uploadedPhotos = this.uploadQueue.filter(p => p.status === 'completed').length;
    const failedPhotos = this.uploadQueue.filter(p => p.status === 'error').length;
    const isUploading = this.uploadQueue.some(p => p.status === 'uploading' || p.status === 'pending');

    this.progressSubject.next({
      totalPhotos,
      uploadedPhotos,
      failedPhotos,
      isUploading
    });
  }

  /**
   * Get photos for a specific project
   */
  getPhotosForProject(projectId: number): UploadingPhoto[] {
    return this.uploadQueue.filter(p => p.projectId === projectId);
  }

  /**
   * Clear completed uploads
   */
  clearCompleted(): void {
    this.uploadQueue = this.uploadQueue.filter(p => p.status !== 'completed');
    this.uploadQueueSubject.next(this.uploadQueue);
    this.updateProgress();
  }

  /**
   * Remove a specific photo from queue
   */
  removePhoto(photoId: string): void {
    const photo = this.uploadQueue.find(p => p.id === photoId);
    if (photo && photo.localPreviewUrl) {
      URL.revokeObjectURL(photo.localPreviewUrl);
    }
    this.uploadQueue = this.uploadQueue.filter(p => p.id !== photoId);
    this.uploadQueueSubject.next(this.uploadQueue);
    this.updateProgress();
  }
}
